import { useGiveSigningConsentMutation } from '@/services/contractSigning';
import { PalmettoButton, PalmettoCheckbox, PalmettoModal } from '@palmetto/dsm-components';
import { useCallback, useMemo, useState } from 'react';

import { CongaPresigningConsentText, Locale } from 'types';

import { Copy, inlineCopy } from './ContractSigningCopy';
import { isConflict } from './contractSigningErrors';

interface ConsentStepProps {
  inviteToken: string;
  locales: Locale[];
  disclosures: CongaPresigningConsentText[];
  onConflict: () => void;
}

const DisclosureText = ({ disclosure }: { disclosure: CongaPresigningConsentText }) => (
  <div
    className="body-300 whitespace-pre-wrap text-text-primary"
    lang={disclosure.language === Locale.es ? 'es' : 'en'}
  >
    {disclosure.text}
  </div>
);

export const ConsentStep = ({ inviteToken, locales, disclosures, onConflict }: ConsentStepProps) => {
  const [giveConsent, { isLoading, isError, error }] = useGiveSigningConsentMutation();
  const [isChecked, setIsChecked] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isDisclosureOpen, setIsDisclosureOpen] = useState(false);

  // Inline, the page names the agreement; the full text lives behind the modal so the step stays short on phones.
  const acceptanceLabel = useMemo(
    () => [...new Set(disclosures.map((disclosure) => disclosure.acceptanceLabel))].join(' / '),
    [disclosures],
  );
  const disclosureIds = useMemo(() => disclosures.map((disclosure) => disclosure.disclosureId), [disclosures]);

  const handleCheckedChange = useCallback((details: { checked: boolean | 'indeterminate' }) => {
    setIsChecked(details.checked === true);
  }, []);
  const openDisclosure = useCallback(() => setIsDisclosureOpen(true), []);
  const closeDisclosure = useCallback(() => setIsDisclosureOpen(false), []);

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);
    if (!isChecked) return;
    try {
      await giveConsent({ inviteToken, disclosureIds }).unwrap();
    } catch (caught) {
      if (isConflict(caught)) {
        setIsChecked(false);
        onConflict();
      }
    }
  }, [isChecked, giveConsent, inviteToken, disclosureIds, onConflict]);

  return (
    <div className="flex flex-col gap-6">
      <Copy copyKey="consentHeading" locales={locales} variant="heading" />
      <PalmettoButton type="button" emphasis="tertiary" layout="hug" onClick={openDisclosure}>
        {/* The bilingual label is wider than a phone screen, and the button never wraps on its own. */}
        <span className="text-left whitespace-normal">{inlineCopy(locales, 'consentReadFull')}</span>
      </PalmettoButton>
      {/* The checkbox's hidden input is absolutely positioned; without this ancestor a click scrolls to the top. */}
      <div className="relative">
        <PalmettoCheckbox
          id="conga-signing-consent"
          label={acceptanceLabel}
          checked={isChecked}
          onCheckedChange={handleCheckedChange}
        />
      </div>
      {hasAttemptedSubmit && !isChecked && <Copy copyKey="consentRequired" locales={locales} variant="error" />}
      <PalmettoButton type="button" emphasis="primary" size="large" loading={isLoading} onClick={handleSubmit}>
        {inlineCopy(locales, 'continue')}
      </PalmettoButton>
      {isError && isConflict(error) && <Copy copyKey="consentChanged" locales={locales} variant="error" />}
      {isError && !isConflict(error) && <Copy copyKey="genericError" locales={locales} variant="error" />}

      <PalmettoModal open={isDisclosureOpen} onClose={closeDisclosure} invertedBackground={false}>
        <div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto">
          {disclosures.map((disclosure) => (
            <DisclosureText key={disclosure.disclosureId} disclosure={disclosure} />
          ))}
          <PalmettoButton type="button" emphasis="secondary" onClick={closeDisclosure}>
            {inlineCopy(locales, 'consentClose')}
          </PalmettoButton>
        </div>
      </PalmettoModal>
    </div>
  );
};
